import { useEffect, useState } from 'react'
import { Button, Modal, Form } from 'react-bootstrap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import Select from 'react-select'
import moment from 'moment'
import { CREATE_COMMISSION_REPORT_DETAIL } from '../../../gql/mutations/commissionReportDetailMutations'
import { parseId } from '../../../helpers'
import { useDebounce } from '../../../../../_metronic/helpers'
import { IResCreateCommissionReportDetail } from '../../../types/commissionReportDetailApi'
import { GET_ALL_USERS_FILTER } from '../../../gql/queries/usersQuery'
import { IResAllUsers } from '../../oneUser/oneUserTypes'
import { GET_ALL_STATES_EU_LIST } from '../../../gql/queries/statesEUQuery'

interface Props {
  isOpen: boolean
  onClose: () => void
  commissionReportId: string
  refetch: () => void
}

const EMPTY_FORM = {
  client: '',
  status: 'APPROVED',
  commissionType: 'COMMISSION',
  rate: 0,
  memberId: '',
  members: 1,
  stateEU: '',
  paidDate: '',
  effectiveMonth: 0,
  effectiveYear: 0,
}

export const ModalAddCommissionReportDetail: React.FC<Props> = ({
  isOpen,
  onClose,
  commissionReportId,
  refetch,
}) => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM })

  const [createDetail, { loading }] = useMutation<IResCreateCommissionReportDetail>(
    CREATE_COMMISSION_REPORT_DETAIL
  )
  const [agentId, setAgent] = useState<string | null>(null)
  const [inputSelect, setInputSelect] = useState('')
  const [options, setOptions] = useState<{ label: string; value: string }[]>([])

  const valueDebounced = useDebounce(inputSelect, 400)
  const [gqlGetUsers, { data: dataUsers, loading: loadingUsers }] = useLazyQuery<IResAllUsers, { name: string }>(GET_ALL_USERS_FILTER)

  // Query para obtener estados
  const { data: statesData } = useQuery<{ allStatesList: { id: string; name: string; code: string }[] }>(GET_ALL_STATES_EU_LIST)

  useEffect(() => {
    if (!valueDebounced) return
    gqlGetUsers({ variables: { name: valueDebounced } })
  }, [valueDebounced, gqlGetUsers])

  useEffect(() => {
    if (!dataUsers) return
    setOptions(
      dataUsers.allUsers.edges.map(({ node }) => ({
        label: `${node.firstName} ${node.lastName} - ${node.email}${
          node.npn ? ` (${node.npn})` : ''
        }`,
        value: node.id,
      }))
    )
  }, [dataUsers])


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleDateChange = (value: string) => {
    if (!value) {
      setFormData(prev => ({
        ...prev,
        paidDate: '',
        effectiveMonth: 0,
        effectiveYear: 0,
      }))
      return
    }
    const date = moment(value)
    setFormData(prev => ({
      ...prev,
      paidDate: value,
      effectiveMonth: date.month() + 1,
      effectiveYear: date.year(),
    }))
  }

  const handleSubmit = async () => {
    try {
      const multiplier = formData.commissionType === 'DEDUCTION' || formData.commissionType === 'CHARGEBACK' ? -1 : 1
      const subtotal = formData.rate * formData.members * multiplier
      const rate = formData.rate * multiplier

      // Preparar input, excluyendo campos vacíos opcionales
      const { paidDate, stateEU, effectiveMonth, effectiveYear, ...baseData } = formData

      const input: Record<string, unknown> = {
        ...baseData,
        rate,
        commissionReport: parseId(commissionReportId),
        user: parseId(agentId ? agentId : ''),
        subtotal,
      }

      // Solo agregar campos opcionales si tienen valor
      if (stateEU) {
        input.stateEU = parseId(stateEU)
      }
      if (effectiveMonth > 0) {
        input.effectiveMonth = effectiveMonth
      }
      if (effectiveYear > 0) {
        input.effectiveYear = effectiveYear
      }

      const variables = { input }

      const { data } = await createDetail({ variables })

      if (data?.commissionReportDetailCreateMutation?.success) {
        toast.success('Commission detail added successfully')
        refetch()
        onClose()
      } else {
        toast.error(data?.commissionReportDetailCreateMutation?.errors?.[0]?.message || 'Error')
      }
    } catch (err) {
      toast.error('Unexpected error creating detail')
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add new records to the report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>Select an agent</Form.Label>
            <Select
              isLoading={loadingUsers}
              placeholder='Type to search...'
              options={options}
              inputValue={inputSelect}
              onInputChange={setInputSelect}
              onChange={selected => setAgent(selected?.value || null)}
            />
          </Form.Group>


          <Form.Group controlId='formClient'>
            <Form.Label>Client / Row title</Form.Label>
            <Form.Control
              type='text'
              name='client'
              value={formData.client}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group controlId='formMemberId' className='mt-2'>
            <Form.Label>Member ID <span className='text-muted'>(optional)</span></Form.Label>
            <Form.Control
              type='text'
              name='memberId'
              value={formData.memberId}
              onChange={handleChange}
              placeholder='Enter member ID'
            />
          </Form.Group>

          <Form.Group controlId='formState' className='mt-2'>
            <Form.Label>State <span className='text-muted'>(optional)</span></Form.Label>
            <Form.Select
              name='stateEU'
              value={formData.stateEU}
              onChange={handleChange}
            >
              <option value=''>Select state...</option>
              {statesData?.allStatesList?.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.code} - {state.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId='formPaidDate' className='mt-2'>
            <Form.Label>Paid Date <span className='text-muted'>(optional)</span></Form.Label>
            <Form.Control
              type='month'
              name='paidDate'
              value={formData.paidDate}
              onChange={(e) => handleDateChange(e.target.value)}
            />
            <Form.Text className='text-muted'>
              Month and year for effective date. If empty, will use report's default.
            </Form.Text>
          </Form.Group>

          <Form.Group controlId='formCommissionType' className='mt-2'>
            <Form.Label>Commission Type</Form.Label>
            <Form.Select
              name='commissionType'
              value={formData.commissionType}
              onChange={handleChange}
            >
              <option value='COMMISSION'>COMMISSION</option>
              <option value='DEDUCTION'>DEDUCTION</option>
              <option value='CHARGEBACK'>Chargeback</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId='formRate' className='mt-2'>
            <Form.Label>Rate</Form.Label>
            <Form.Control
              type='number'
              name='rate'
              value={formData.rate}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type='number'
              name='members'
              value={formData.members}
              onChange={handleChange}
              required
            />
          </Form.Group>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
